import { Flex, Button, BoxProps, ButtonProps } from '@chakra-ui/react'
import useTranslation from 'next-translate/useTranslation'
import { useStore } from '@store'
import { observer } from 'mobx-react-lite'
import { IExhibitionAndEventActiveCollectionName } from '@interfaces'
import { useRouter } from 'next/router'
import { MutableRefObject } from 'react'
import { createNavLink } from '@utils'

type ActiveWithAll = IExhibitionAndEventActiveCollectionName | 'all'

interface IExhibitionAndEventTabButtonGroups {
  containerProps?: BoxProps
  buttonProps?: ButtonProps
  containerRef?: MutableRefObject<HTMLDivElement>
  isLoading?: boolean
  // When true, do not route. Instead, call onChangeActive and rely on activeOverride for current state.
  disableRouting?: boolean
  // When true, shows an "All" button as the first tab.
  showAllButton?: boolean
  onChangeActive?: (active: ActiveWithAll) => void
  activeOverride?: ActiveWithAll
}

const ExhibitionAndEventTabButtonGroups = observer(
  ({
    containerProps,
    buttonProps,
    containerRef,
    isLoading = false,
    disableRouting = false,
    showAllButton = false,
    onChangeActive,
    activeOverride,
  }: IExhibitionAndEventTabButtonGroups) => {
    const router = useRouter()
    const { t } = useTranslation()
    const {
      exhibitionAndEvents: {
        activeCollection,
        setActiveCollection,
        setDisplaySortModal,
        sortResult,
        setSortResult,
        setPage,
      },
    } = useStore()

    const currentActive = (activeOverride as ActiveWithAll) ?? activeCollection
    const getStableButtonStyles = (isActive: boolean) => ({
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: isActive ? 'red.primary' : 'black.primary3',
      boxSizing: 'border-box' as const,
    })

    const typeOnClickAction = (nextActive: ActiveWithAll) => {
      const activeSort = nextActive === 'online' ? 'current' : sortResult
      setDisplaySortModal(false)

      if (disableRouting) {
        onChangeActive && onChangeActive(nextActive)
        return
      }

      const query = router.query
      query.activeCollection = nextActive
      query.sort = activeSort
      query.page = '1'

      setSortResult(activeSort)
      if (nextActive !== 'all') {
        setActiveCollection(nextActive)
      }
      setPage('1')

      router.push(
        {
          pathname: createNavLink('exhibitionAndEvent', router.locale),
          query: query,
        },
        undefined,
        { scroll: false }
      )

      const yCoordinate =
        containerRef?.current?.getBoundingClientRect().top + window.pageYOffset
      const yOffset = -40
      window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' })
    }

    return (
      <Flex
        gridArea="tabButtonGroup"
        overflowX="auto"
        mt="20px"
        mx="padding.primary"
        sx={{
          '::-webkit-scrollbar': {
            height: '6px',
          },
          '::-webkit-scrollbar-thumb': {
            background: 'red.primary',
          },
          '&>*+*': {
            ml: '10px',
          },
        }}
        {...containerProps}
      >
        {showAllButton && (
          <Button
            display="inline-flex"
            height="50px"
            fontSize="sm"
            variant={currentActive === 'all' ? 'secondary' : 'outline'}
            onClick={() => typeOnClickAction('all')}
            disabled={isLoading}
            _disabled={{
              opacity: 1,
              cursor: 'not-allowed',
              boxShadow: 'none',
              _hover: {
                background: 'red.primary3',
              },
            }}
            {...getStableButtonStyles(currentActive === 'all')}
            {...buttonProps}
          >
            {t('exhibition-and-event:home.eventTypes.all')}
          </Button>
        )}
        <Button
          display="inline-flex"
          height="50px"
          fontSize="sm"
          variant={currentActive === 'exhibitions' ? 'secondary' : 'outline'}
          onClick={() => typeOnClickAction('exhibitions')}
          disabled={isLoading}
          _disabled={{
            opacity: 1,
            cursor: 'not-allowed',
            boxShadow: 'none',
            _hover: {
              background: 'red.primary3',
            },
          }}
          {...getStableButtonStyles(currentActive === 'exhibitions')}
          {...buttonProps}
        >
          {t('exhibition-and-event:home.eventTypes.exhibitions')}
        </Button>
        <Button
          display="inline-flex"
          height="50px"
          fontSize="sm"
          variant={currentActive === 'events' ? 'secondary' : 'outline'}
          onClick={() => typeOnClickAction('events')}
          disabled={isLoading}
          _disabled={{
            opacity: 1,
            cursor: 'not-allowed',
            boxShadow: 'none',
            _hover: {
              background: 'red.primary3',
            },
          }}
          {...getStableButtonStyles(currentActive === 'events')}
          {...buttonProps}
        >
          {t('exhibition-and-event:home.eventTypes.events')}
        </Button>
        <Button
          display="inline-flex"
          height="50px"
          fontSize="sm"
          variant={currentActive === 'learning' ? 'secondary' : 'outline'}
          onClick={() => typeOnClickAction('learning')}
          disabled={isLoading}
          _disabled={{
            opacity: 1,
            cursor: 'not-allowed',
            boxShadow: 'none',
            _hover: {
              background: 'red.primary3',
            },
          }}
          {...getStableButtonStyles(currentActive === 'learning')}
          {...buttonProps}
        >
          {t('exhibition-and-event:home.eventTypes.learning')}
        </Button>
        <Button
          display="inline-flex"
          height="50px"
          fontSize="sm"
          variant={currentActive === 'children' ? 'secondary' : 'outline'}
          onClick={() => typeOnClickAction('children')}
          disabled={isLoading}
          _disabled={{
            opacity: 1,
            cursor: 'not-allowed',
            boxShadow: 'none',
            _hover: {
              background: 'red.primary3',
            },
          }}
          {...getStableButtonStyles(currentActive === 'children')}
          {...buttonProps}
        >
          {t('exhibition-and-event:home.eventTypes.children')}
        </Button>
        <Button
          //display="inline-flex"
          display="none"
          height="50px"
          fontSize="sm"
          variant={currentActive === 'workshop' ? 'secondary' : 'outline'}
          onClick={() => typeOnClickAction('workshop')}
          disabled={isLoading}
          _disabled={{
            opacity: 1,
            cursor: 'not-allowed',
            boxShadow: 'none',
            _hover: {
              background: 'red.primary3',
            },
          }}
          {...getStableButtonStyles(currentActive === 'workshop')}
          {...buttonProps}
        >
          {t('exhibition-and-event:home.eventTypes.workshops')}
        </Button>
        <Button
          display="inline-flex"
          height="50px"
          fontSize="sm"
          variant={currentActive === 'tours' ? 'secondary' : 'outline'}
          onClick={() => typeOnClickAction('tours')}
          disabled={isLoading}
          _disabled={{
            opacity: 1,
            cursor: 'not-allowed',
            boxShadow: 'none',
            _hover: {
              background: 'red.primary3',
            },
          }}
          {...getStableButtonStyles(currentActive === 'tours')}
          {...buttonProps}
        >
          {t('exhibition-and-event:home.eventTypes.tours')}
        </Button>
        <Button
          display="none"
          height="50px"
          fontSize="sm"
          variant={currentActive === 'online' ? 'secondary' : 'outline'}
          onClick={() => typeOnClickAction('online')}
          disabled={isLoading}
          _disabled={{
            opacity: 1,
            cursor: 'not-allowed',
            boxShadow: 'none',
            _hover: {
              background: 'red.primary3',
            },
          }}
          {...getStableButtonStyles(currentActive === 'online')}
          {...buttonProps}
        >
          {t('exhibition-and-event:home.eventTypes.online')}
        </Button>
      </Flex>
    )
  }
)

export default ExhibitionAndEventTabButtonGroups
